import request from '../request';

import { url } from './config';
import type {
    AddCommentBody,
    CreateTicketBody,
    ListTicketsQuery,
    Ticket,
    TicketCommentWithUrls,
    TicketWithDetail,
    UpdateTicketBody
} from './types';

const handleError = (error: unknown) => {
    console.error(`[tickets] api error:`, error);
    throw error;
};

const ticketsApi = {
    /** Any authenticated user. */
    async createTicket(body: CreateTicketBody): Promise<{ _id: number; ticketNumber: string }> {
        try {
            const res = await request.post(url, body);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Any authenticated user. Returns the stored S3 key to attach to the ticket. */
    async uploadScreenshot(file: Blob): Promise<{ screenshotS3Key: string }> {
        try {
            const form = new FormData();
            form.append('screenshot', file);
            const res = await request.post(`${url}/screenshot`, form);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** The caller's own tickets — no permission required. */
    async listMyTickets(query: ListTicketsQuery = {}): Promise<Ticket[]> {
        try {
            const res = await request.get(`${url}/mine`, { params: query });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /**
     * The caller's own tickets that need them — awaiting their reply, or carrying a reply they have
     * not read. No permission required.
     */
    async countMyAttention(): Promise<number> {
        try {
            const res = await request.get(`${url}/mine/attention-count`);
            return Number(res.data?.count) || 0;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Detail for one of the caller's own tickets. Internal notes are not included. */
    async getMyTicket(id: number): Promise<TicketWithDetail> {
        try {
            const res = await request.get(`${url}/mine/${id}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Soft delete — moves the caller's own ticket to their bin; recoverable via restoreMyTicket. */
    async deleteMyTicket(id: number): Promise<{ _id: number }> {
        try {
            const res = await request.delete(`${url}/mine/${id}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Permanent — also deletes the ticket's screenshots from S3. Bin only. */
    async purgeMyTicket(id: number): Promise<{ _id: number; screenshotsDeleted: number }> {
        try {
            const res = await request.delete(`${url}/mine/${id}/purge`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async restoreMyTicket(id: number): Promise<{ _id: number }> {
        try {
            const res = await request.post(`${url}/mine/${id}/restore`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Requires system.tickets:view; scoped to the caller's tenant unless super user. */
    async listTickets(query: ListTicketsQuery = {}): Promise<Ticket[]> {
        try {
            const res = await request.get(url, { params: query });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** `system.tickets:view`. Active tickets still in `new`, scoped to the caller's tenant. */
    async countNewTickets(): Promise<number> {
        try {
            const res = await request.get(`${url}/new-count`);
            return Number(res.data?.count) || 0;
        } catch (error) {
            return handleError(error);
        }
    },

    /** `system.tickets:view`. Active tickets the reporter replied on since staff last read them. */
    async countRepliedTickets(): Promise<number> {
        try {
            const res = await request.get(`${url}/replied-count`);
            return Number(res.data?.count) || 0;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Super-user only. */
    async getTicket(id: number): Promise<TicketWithDetail> {
        try {
            const res = await request.get(`${url}/${id}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Super-user only. */
    async updateTicket(id: number, body: UpdateTicketBody): Promise<Ticket> {
        try {
            const res = await request.patch(`${url}/${id}`, body);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Requires system.tickets:edit — soft delete from the triage board. */
    async deleteTicket(id: number): Promise<{ _id: number }> {
        try {
            const res = await request.delete(`${url}/${id}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async restoreTicket(id: number): Promise<{ _id: number }> {
        try {
            const res = await request.post(`${url}/${id}/restore`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Permanent — also deletes the ticket's screenshots from S3. Bin only. */
    async purgeTicket(id: number): Promise<{ _id: number; screenshotsDeleted: number }> {
        try {
            const res = await request.delete(`${url}/${id}/purge`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /**
     * Requires system.tickets:edit. `statusChanged` reports whether a shared reply moved the ticket to
     * 'awaiting_reporter', so the dialog can reload the status rather than guess it.
     */
    async addComment(
        id: number,
        body: AddCommentBody
    ): Promise<TicketCommentWithUrls & { statusChanged?: boolean }> {
        try {
            const res = await request.post(`${url}/${id}/comments`, body);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /**
     * A reporter's reply on their own ticket. `reopened` reports whether the reply released
     * 'awaiting_reporter' or reopened a closed ticket, so the UI can say so rather than silently
     * changing the status under the reader.
     */
    async addMyComment(
        id: number,
        body: AddCommentBody
    ): Promise<TicketCommentWithUrls & { reopened?: boolean }> {
        try {
            const res = await request.post(`${url}/mine/${id}/comments`, body);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    }
};

export default ticketsApi;
